import type { CommandAction } from './types';

/** Read `import.meta.env.DEV` defensively — in tests / SSR it can be missing. */
function isDev(): boolean {
  const env = (import.meta as ImportMeta).env as
    | Record<string, unknown>
    | undefined;
  return env?.DEV === true;
}

/**
 * Singleton registry for command-palette actions.
 *
 * Uses an external-store pattern (`subscribe` + immutable snapshots) so
 * components can subscribe via `useSyncExternalStore` and only re-render
 * when the action set actually changes — not on every keystroke in the
 * palette.
 *
 * Duplicate-id registration warns in dev (the second register wins).
 */
class ActionRegistry {
  private actions = new Map<string, CommandAction>();
  private listeners = new Set<() => void>();
  private snapshot: ReadonlyArray<CommandAction> = [];

  register(action: CommandAction): void {
    if (this.actions.has(action.id) && isDev()) {
      console.warn(
        `[command-palette] duplicate action id "${action.id}" — overwriting`,
      );
    }
    this.actions.set(action.id, action);
    this.recomputeSnapshot();
  }

  unregister(id: string): void {
    if (this.actions.delete(id)) {
      this.recomputeSnapshot();
    }
  }

  list = (): ReadonlyArray<CommandAction> => {
    return this.snapshot;
  };

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Test-only: drop everything. */
  __resetForTests(): void {
    this.actions.clear();
    this.recomputeSnapshot();
  }

  private recomputeSnapshot(): void {
    this.snapshot = Array.from(this.actions.values());
    this.listeners.forEach((listener) => {
      listener();
    });
  }
}

export const actionRegistry = new ActionRegistry();
